import { getInitialSoftwarePricing } from '../utils/configUtilities';
import { getCurrentQuarter, isSkuAvailable } from '../constants/quarterConfig';
import { migrateQuoteConfig } from '../utils/migration.jsx';
import { log } from '../utils/devLog';

/**
 * Prepares a saved (loaded) configuration to be merged over the previous
 * session's flat config, ahead of business-rule application.
 *
 * Pulled verbatim out of useConfigurationLogic's `loadConfig` — specifically
 * everything that ran INSIDE its `setDomain` updater BEFORE `runRules`
 * (CONF-112 Task 9). The hook and the MCP Lambda's headless pipeline both
 * call this same function so a loaded quote resolves identically in either
 * place.
 *
 * Takes the RAW `savedConfig` (not pre-migrated) and calls `migrateQuoteConfig`
 * itself — the hook used to call it separately, outside `setDomain`; folding
 * it in here keeps the call count and its ordering relative to the hook's
 * pricing lock unchanged (still exactly once, still between the lock being
 * set and released).
 *
 * @param {Object} prevFlat - The flat config of the PREVIOUS session (the
 *   hook passes `toFlatConfig(prevDomain)`).
 * @param {Object} savedConfig - The raw saved configuration to load.
 * @param {Object} productDb - Product database for pricing lookups.
 * @param {Object} vendor - The active vendor (capabilities + businessRules).
 * @returns {Object} The flat config to run business rules against.
 */
export const prepareLoadedConfig = (prevFlat, savedConfig, productDb, vendor) => {
  // Migrate if needed
  const migratedConfig = migrateQuoteConfig(savedConfig);

  const prev = prevFlat;
  const newConfig = {
    ...prev,
    ...migratedConfig,
    hardwareVendor: migratedConfig.hardwareVendor || prev.hardwareVendor
  };

  // Drive part numbers are OPTIONAL flat keys, stamped only on explicit
  // dropdown selection — a quote saved with the default selection carries
  // capacities but no PN keys. The spread above would let the PREVIOUS
  // session's PN survive such a load, and findCurrentMapping prefers PN
  // over capacity, so the UI would keep showing the stale drive. A loaded
  // quote fully defines the selection surface: clear what it doesn't set,
  // then stamp the PN its capacity implies (quarter-aware, first match —
  // TLC before QLC at a shared capacity), so downstream consumers — the dropdown
  // lookup AND the quarter-change SKU reset rule, which resets on
  // unavailable PN — see a fully-specified selection.
  //
  // CONF-210: afaNodeModel is optional in exactly the same way (absent
  // means the single-CPU node), so a legacy blob or an older configV2
  // row that predates the field must load as single-CPU rather than
  // inherit this session's Dual CPU choice.
  for (const key of ['afaSsdPartNumber', 'hybridSsdPartNumber', 'hybridHddPartNumber', 'afaNodeModel']) {
    if (!(key in migratedConfig)) delete newConfig[key];
  }
  const pMap = vendor.capabilities.productMappings;
  const stampPartNumber = (key, mappings, capacity) => {
    if (newConfig[key] !== undefined) return;
    const match = (mappings || []).find(m =>
      m.capacity === capacity && m.enabled !== false &&
      isSkuAvailable(m.availableFrom, newConfig.quarter));
    if (match) newConfig[key] = match.partNumber;
  };
  stampPartNumber('afaSsdPartNumber', pMap.afaSsd, newConfig.afaVpodSsdCapacity);
  stampPartNumber('hybridSsdPartNumber', pMap.hybridSsd, newConfig.hybridVpodSsdCapacity);
  stampPartNumber('hybridHddPartNumber', pMap.hybridHdd?.[newConfig.jbodSize], newConfig.hybridHddCapacity);

  const regMultiplier = vendor.businessRules.registrationMultiplier;

  // Handle pricing specially when loading
  if (migratedConfig.ssdSoftware !== undefined && migratedConfig.hddSoftware !== undefined) {
    // Preserve the EXACT saved pricing
    newConfig.ssdSoftware = migratedConfig.ssdSoftware;
    newConfig.hddSoftware = migratedConfig.hddSoftware;

    // If saved config has pricing, derive base prices
    const isLoadedNoReg = migratedConfig.isNoReg !== undefined ? migratedConfig.isNoReg : true;

    if (isLoadedNoReg) {
      // Prices in saved config are base prices
      newConfig.baseSsdSoftware = migratedConfig.ssdSoftware;
      newConfig.baseHddSoftware = migratedConfig.hddSoftware;
    } else {
      // Prices in saved config have 1.4x multiplier, calculate base
      newConfig.baseSsdSoftware = Number((migratedConfig.ssdSoftware / 1.4).toFixed(2));
      newConfig.baseHddSoftware = Number((migratedConfig.hddSoftware / 1.4).toFixed(2));
    }
    // Recalculate License Unit Price
    newConfig.ssdLicenseUnitPrice = newConfig.ssdSoftware - newConfig.ssdSupportUnitPrice;
    newConfig.hddLicenseUnitPrice = newConfig.hddSoftware - newConfig.hddSupportUnitPrice;

    log('Loaded pricing from saved config:', {
      ssdSoftware: newConfig.ssdSoftware,
      hddSoftware: newConfig.hddSoftware,
      baseSsdSoftware: newConfig.baseSsdSoftware,
      baseHddSoftware: newConfig.baseHddSoftware,
      isNoReg: newConfig.isNoReg
    });
  } else {
    // No saved pricing, get current pricing from productDb
    const currentPricing = getInitialSoftwarePricing(
      productDb,
      migratedConfig.quarter || getCurrentQuarter(),
      migratedConfig.serviceOption || "standard",
      vendor,
      migratedConfig.subscriptionMonths
    );

    newConfig.baseSsdSoftware = currentPricing.ssdSoftware;
    newConfig.baseHddSoftware = currentPricing.hddSoftware;

    // Apply registration multiplier
    newConfig.ssdSoftware = newConfig.isNoReg
      ? currentPricing.ssdSoftware
      : Number((currentPricing.ssdSoftware * regMultiplier).toFixed(2));
    newConfig.hddSoftware = newConfig.isNoReg
      ? currentPricing.hddSoftware
      : Number((currentPricing.hddSoftware * regMultiplier).toFixed(2));

    // Recalculate the License Unit Prices
    newConfig.ssdLicenseUnitPrice = newConfig.ssdSoftware - newConfig.ssdSupportUnitPrice;
    newConfig.hddLicenseUnitPrice = newConfig.hddSoftware - newConfig.hddSupportUnitPrice;
  }

  return newConfig;
};
